import type { CollectionBindingOverride } from '@vibesites/runtime';

/**
 * Curates a property photo gallery into Pathfinder tour order (flat list):
 * 1. Hero — strongest overall first impression
 * 2. Property arrival / exterior — monument, building views, ≤3 drones
 * 3. Amenities first, then resident/community spaces (clubhouse, coworking)
 * 4. Apartment experience — staged model first, then limited vacant detail
 *
 * Titles in this portfolio are usually "Community" or "Apartment"; the
 * subject lives in the description and alt text. Caps keep drone photography
 * to three images and keep vacant apartments from crowding out virtually
 * staged photography. Photos we cannot place stay at the end.
 */

export interface CuratableGalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  alt_text?: string | null;
  title?: string | null;
  description?: string | null;
}

export type GalleryTab = 'all' | 'community' | 'apartment';

export interface CuratedGallery<T> {
  all: T[];
  community: T[];
  apartment: T[];
}

type Stage =
  | 'monument'
  | 'exterior'
  | 'drone'
  | 'clubhouse'
  | 'coworking'
  | 'fitness'
  | 'pool'
  | 'grill'
  | 'pet'
  | 'recreation'
  | 'package'
  | 'otherAmenity'
  | 'lifestyle'
  | 'staged'
  | 'apartment'
  | 'vacant'
  | 'other';

type PhotoTab = 'community' | 'apartment' | null;

interface Classified<T> {
  photo: T;
  index: number;
  stage: Stage;
  room: number;
  tab: PhotoTab;
  /** Caption + filename tokens used for subject matching. */
  text: string;
  /** Title / description / alt only (no URLs). */
  caption: string;
  captionKey: string;
}

const ROOM_DEFAULT = 80;
const MAX_DRONE = 3;
const MAX_VACANT_WITH_STAGED = 8;
const MAX_APARTMENT_WITHOUT_STAGED = 16;
const MAX_UNKNOWN_APARTMENT = 12;

const STAGE_CAP: Partial<Record<Stage, number>> = {
  monument: 2,
  exterior: 4,
  drone: MAX_DRONE,
  clubhouse: 4,
  coworking: 3,
  fitness: 3,
  pool: 4,
  grill: 3,
  pet: 3,
  recreation: 3,
  package: 2,
  lifestyle: 3,
};

/**
 * Lower sorts earlier. After arrival/exterior: amenities (pool, fitness, outdoor,
 * recreation) before indoor community spaces (clubhouse, coworking, lifestyle).
 * Unspecified and vacant apartments share a rank so rooms interleave.
 */
const STAGE_RANK: Record<Stage, number> = {
  monument: 10,
  exterior: 20,
  drone: 30,
  // Amenities / outdoor & shared facilities
  fitness: 40,
  pool: 50,
  grill: 60,
  pet: 70,
  recreation: 80,
  package: 90,
  otherAmenity: 100,
  // Resident / community spaces after amenities
  clubhouse: 110,
  coworking: 120,
  lifestyle: 130,
  staged: 200,
  apartment: 210,
  vacant: 210,
  other: 300,
};

const DRONE = /\b(drones?|aerials?|bird'?s?\s*-?\s*eye|from\s+above)\b/;
const MONUMENT =
  /\b(monuments?|property signs?|entry signs?|community signs?|signage|monument signs?)\b/;
/** Building / arrival / setting shots — rule #2 after the hero. */
const EXTERIOR =
  /\b(exteriors?|façades?|facades?|elevations?|streetscapes?|arrivals?|buildings?|courtyards?|porte[\s-]?coch[eè]re|driveways?|architecture|landscap(?:e|ing)|twilight|dusk|sunset|golden\s+hour|community\s+entrances?|leasing\s+entrances?|(?:building|property|community)\s+views?)\b|\bbuilding\s+exterior\b|\bproperty\s+exterior\b/;
const STAGED =
  /virtually\s+staged|virtual(?:ly)?\s+stag|virtually\s+furnish|\bstaged\b|\bfurnished\b|\bmodel\s+(?:apartments?|units?|homes?|kitchens?|livings?|bedrooms?|baths?)/;
const VACANT = /\bvacants?\b|\bunfurnished\b|\bempty\s+(?:apartments?|units?|kitchens?|rooms?|livings?)/;
/** Logos / brand marks that should never appear as page heroes or gallery tour photos. */
const LOGO = /\blogos?\b|\bwordmarks?\b|\blogotypes?\b|\bbrand\s+marks?\b/;
/**
 * Unit floor-plan drawings / line art (not community site maps). These belong
 * on floorplan listings, not marketing heroes or lifestyle galleries.
 */
const FLOOR_PLAN_LAYOUT =
  /\bfloor\s*-?\s*plans?\b|\bfloorplans?\b|\bunit\s*-?\s*plans?\b|\bblueprints?\b|\bschemat(?:ic|ics)\b|\bline\s*arts?\b|\blayout\s+drawings?\b/;
const CLUBHOUSE = /\b(clubhouses?|club\s*rooms?|resident lounges?|resident clubs?|leasing offices?)\b|\blobb(?:y|ies)\b/;
const LOUNGE = /\blounges?\b/;
const COWORK = /\b(cowork(?:ing)?|co-working|business centers?|conference rooms?)\b/;
const FITNESS = /\b(fitness|gyms?|workouts?|exercise rooms?|yoga)\b/;
const POOL = /\b(pools?|swimming|hot tubs?|cabanas?|sundecks?|sun decks?)\b|\bspas?\b/;
const GRILL = /\b(grills?|bbqs?|barbecues?|barbeques?|outdoor kitchens?|fire\s*pits?|picnics?)\b/;
/** Patio / outdoor dining — amenity space, not a property-establishing hero. */
const OUTDOOR_SEATING =
  /\b(patios?|umbrellas?|outdoor\s+(?:seat(?:ing|s)?|dining|lounges?)|picnic\s+(?:tables?|areas?)|cafe\s+tables?|bistro|terrace\s+seat|fireside\s+patios?|seating\s+areas?)/;
const PET = /\b(dog parks?|pet parks?|pet spas?|pet wash(?:es)?|bark parks?|dog wash(?:es)?)\b/;
const RECREATION =
  /\b(playgrounds?|basketball|tennis|volleyball|sports? courts?|game rooms?|billiards?|recreation|putting greens?|bocce)\b/;
const PACKAGE = /\b(packages?|parcels?|mail\s*rooms?)\b|\b(?:package|parcel)\s+lockers?\b/;
const APARTMENT_ROOM =
  /\b(kitchens?|bedrooms?|bathrooms?|baths?|living\s+(?:rooms?|spaces?|areas?)|dining\s+rooms?|islands?|peninsulas?|appliances?)\b/;
/** Full-building / streetscape cues — strongest Pathfinder hero candidates. */
const BUILDING_ESTABLISHING =
  /\b(façades?|facades?|elevations?|streetscapes?|street[\s-]?level|architecture|(?:building|property|community)\s+(?:exteriors?|views?)|buildings?)\b|\bexteriors?\b/;
/** Courtyard / landscape detail — fine in arrival, weak as the lead hero. */
const SETTING_DETAIL = /\b(courtyards?|landscap(?:e|ing)|driveways?|porte[\s-]?coch[eè]re)\b/;

const ROOM_PATTERNS: Array<{ rank: number; pattern: RegExp }> = [
  { rank: 10, pattern: /\b(entr(?:y|ance)|foyers?|entryways?)\b/ },
  { rank: 20, pattern: /\b(living\s+(?:rooms?|spaces?|areas?)|great\s+rooms?|family\s+rooms?)\b/ },
  { rank: 30, pattern: /\b(dining\s+rooms?)\b/ },
  { rank: 40, pattern: /\b(kitchens?|islands?|peninsulas?|appliances?)\b/ },
  { rank: 50, pattern: /\b(bedrooms?|primary\s+suites?|master\s+bedrooms?)\b/ },
  { rank: 60, pattern: /\b(bathrooms?|baths?|powder\s+rooms?)\b/ },
  {
    rank: 70,
    pattern:
      /\b(laundr(?:y|ies)|closets?|balcon(?:y|ies)|patios?|fireplaces?|windows?|ceiling\s+fans?|finishes|finish\s+packages?)\b/,
  },
];

export function galleryPhotoSrc(photo: CuratableGalleryPhoto): string {
  return photo.image || photo.original_url || '';
}

/** Editor reorder wins over the tour sequence. */
export function hasCustomGalleryOrder(
  override: CollectionBindingOverride | null | undefined,
): boolean {
  if (!override) return false;
  return override.orderedIds != null || (override.ordering?.length ?? 0) > 0;
}

function normalizePhotoText(parts: Array<string | null | undefined>): string {
  return parts
    .map((value) => (value || '').toLowerCase())
    .join(' ')
    .replace(/[/_+.-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Title / description / alt only — used for bare-caption fallbacks. */
function photoCaptionText(photo: CuratableGalleryPhoto): string {
  return normalizePhotoText([photo.title, photo.description, photo.alt_text]);
}

/**
 * Caption plus URL filename tokens. CDN paths often include subject cues
 * (patio, exterior, drone) when CMS captions are thin.
 */
function photoText(photo: CuratableGalleryPhoto): string {
  const urlName = [photo.image, photo.original_url]
    .map((value) => {
      if (!value) return '';
      try {
        const path = value.split('?')[0] ?? value;
        const leaf = path.split('/').pop() ?? path;
        return leaf;
      } catch {
        return value;
      }
    })
    .join(' ');
  return normalizePhotoText([photo.title, photo.description, photo.alt_text, urlName]);
}

/** True when caption/title text looks like a logo or brand mark. */
export function isLogoPhoto(photo: CuratableGalleryPhoto): boolean {
  return LOGO.test(photoText(photo));
}

/** True when caption/title text looks like a unit floor-plan drawing / line art. */
export function isFloorPlanLayoutPhoto(photo: CuratableGalleryPhoto): boolean {
  return FLOOR_PLAN_LAYOUT.test(photoText(photo));
}

/**
 * Logos and unit floor-plan layouts are not marketing photography — exclude
 * them from heroes, gallery tours, and lifestyle pickers.
 */
export function isNonMarketingGraphic(photo: CuratableGalleryPhoto): boolean {
  return isLogoPhoto(photo) || isFloorPlanLayoutPhoto(photo);
}

/** Virtually staged / furnished / model-unit photography. */
export function isStagedPhoto(photo: CuratableGalleryPhoto): boolean {
  return STAGED.test(photoText(photo));
}

function captionKey(photo: CuratableGalleryPhoto): string {
  const raw = (photo.description || photo.alt_text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  return raw.length >= 3 ? raw : '';
}

function roomRank(text: string): number {
  let best = ROOM_DEFAULT;
  for (const { rank, pattern } of ROOM_PATTERNS) {
    if (rank < best && pattern.test(text)) best = rank;
  }
  return best;
}

/** Shared amenity / resident-space cues (rule #3) — checked before exterior fallback. */
function looksLikeAmenity(text: string): boolean {
  return (
    PET.test(text) ||
    PACKAGE.test(text) ||
    POOL.test(text) ||
    GRILL.test(text) ||
    OUTDOOR_SEATING.test(text) ||
    CLUBHOUSE.test(text) ||
    (LOUNGE.test(text) && !POOL.test(text)) ||
    COWORK.test(text) ||
    FITNESS.test(text) ||
    RECREATION.test(text)
  );
}

function communityStage(text: string, title: string, caption: string): Stage {
  if (DRONE.test(text)) return 'drone';
  if (MONUMENT.test(text)) return 'monument';
  if (PET.test(text)) return 'pet';
  if (PACKAGE.test(text)) return 'package';
  if (POOL.test(text)) return 'pool';
  // Patio / umbrella seating is outdoor entertainment, not an establishing exterior.
  if (GRILL.test(text) || OUTDOOR_SEATING.test(text)) return 'grill';
  if (CLUBHOUSE.test(text) || (LOUNGE.test(text) && !POOL.test(text))) return 'clubhouse';
  if (COWORK.test(text)) return 'coworking';
  if (FITNESS.test(text)) return 'fitness';
  if (RECREATION.test(text)) return 'recreation';
  if (EXTERIOR.test(text)) return 'exterior';
  if (title.includes('lifestyle') || /\blifestyle\b/.test(text)) return 'lifestyle';
  // Bare "Community" captions (little/no subject text) are usually arrival or
  // building shots. Keep them in rule #2 so the tour doesn't jump hero → amenities.
  if (title.includes('community') && !looksLikeAmenity(text)) {
    const subject = caption.replace(/\bcommunity\b/g, '').trim();
    if (subject.length < 8) return 'exterior';
  }
  return 'otherAmenity';
}

function looksLikeCommunity(text: string): boolean {
  return (
    DRONE.test(text) ||
    MONUMENT.test(text) ||
    EXTERIOR.test(text) ||
    looksLikeAmenity(text) ||
    /\blifestyle\b/.test(text)
  );
}

function looksLikeApartment(text: string): boolean {
  return APARTMENT_ROOM.test(text) || STAGED.test(text) || VACANT.test(text);
}

function apartmentStage(text: string): Stage {
  if (STAGED.test(text)) return 'staged';
  if (VACANT.test(text)) return 'vacant';
  return 'apartment';
}

function classify<T extends CuratableGalleryPhoto>(photo: T, index: number): Classified<T> {
  const text = photoText(photo);
  const caption = photoCaptionText(photo);
  const title = (photo.title || '').toLowerCase();
  const apartmentTitle = title.includes('apartment');
  const communityTitle = title.includes('community') || title.includes('lifestyle');
  const apartmentish = apartmentTitle || looksLikeApartment(text);
  const communityish = communityTitle || looksLikeCommunity(text);

  let stage: Stage;
  let tab: PhotoTab;
  if (apartmentTitle || (apartmentish && !communityish)) {
    stage = apartmentStage(text);
    tab = 'apartment';
  } else if (communityish) {
    stage = communityStage(text, title, caption);
    tab = 'community';
  } else {
    stage = 'other';
    tab = null;
  }

  return {
    photo,
    index,
    stage,
    room: tab === 'apartment' ? roomRank(text) : 0,
    tab,
    text,
    caption,
    captionKey: captionKey(photo),
  };
}

/**
 * Pathfinder hero = strongest overall first impression of the community.
 * Prefer a full building / streetscape exterior (or standout amenity) over
 * patio seating, courtyard vignettes, and other detail crops.
 */
function isWeakLead<T>(item: Classified<T>): boolean {
  // Monument / entry signs and patio seating are arrival context, not the lead.
  return (
    item.stage === 'monument' ||
    MONUMENT.test(item.text) ||
    item.stage === 'grill' ||
    OUTDOOR_SEATING.test(item.text)
  );
}

function heroScore<T>(item: Classified<T>): number {
  if (item.stage === 'drone' || item.tab === 'apartment') return 0;
  if (isWeakLead(item)) return 0;
  if (/\bhero\b/.test(item.text)) return 100;

  const twilight = /\b(twilight|dusk|sunset|golden\s+hour)\b/.test(item.text);
  const buildingLead = BUILDING_ESTABLISHING.test(item.text);
  const settingDetail = SETTING_DETAIL.test(item.text);
  const subject = item.caption.replace(/\bcommunity\b/g, '').trim();
  const bareCaption = subject.length < 8;

  if (buildingLead && (item.stage === 'exterior' || item.stage === 'otherAmenity' || item.stage === 'other')) {
    return twilight ? 92 : 85;
  }
  if (twilight && (item.stage === 'exterior' || item.stage === 'pool' || item.stage === 'clubhouse')) {
    return settingDetail ? 55 : 80;
  }
  if (item.stage === 'exterior') {
    // Courtyard / landscape detail establishes setting but is a weak lead.
    if (settingDetail) return 42;
    // Bare "Community" exteriors stay in the tour but shouldn't beat a real facade.
    if (bareCaption) return 30;
    return 70;
  }
  if (item.stage === 'clubhouse') return 60;
  if (item.stage === 'pool') return 55;
  if (item.tab === 'community') return 20;
  return 0;
}

function pickHero<T>(items: Classified<T>[]): Classified<T> | null {
  let best: Classified<T> | null = null;
  let bestScore = 0;
  for (const item of items) {
    const score = heroScore(item);
    // Prefer building-establishing shots when scores tie (e.g. several exteriors).
    if (
      score > bestScore ||
      (score === bestScore &&
        best != null &&
        score > 0 &&
        BUILDING_ESTABLISHING.test(item.text) &&
        !BUILDING_ESTABLISHING.test(best.text))
    ) {
      best = item;
      bestScore = score;
    }
  }
  if (best && bestScore > 0) return best;
  // Fall back to an establishing exterior — never lead with a monument sign or patio.
  return (
    items.find((item) => item.stage === 'exterior' && BUILDING_ESTABLISHING.test(item.text)) ??
    items.find((item) => item.stage === 'exterior' && !isWeakLead(item)) ??
    items.find((item) => item.stage === 'clubhouse' || item.stage === 'pool') ??
    items.find((item) => item.stage === 'staged') ??
    items.find((item) => !isWeakLead(item)) ??
    items[0] ??
    null
  );
}

/**
 * Cap counts apply to photos after the hero only. The hero is already removed
 * from `items`, so seeding its stage would steal an arrival/exterior slot and
 * make rule #2 look empty when the lead image was an exterior.
 */
function applyStageCaps<T>(items: Classified<T>[]): Classified<T>[] {
  const counts = new Map<Stage, number>();

  const kept: Classified<T>[] = [];
  for (const item of items) {
    const cap = STAGE_CAP[item.stage];
    if (cap == null) {
      kept.push(item);
      continue;
    }
    const used = counts.get(item.stage) ?? 0;
    if (used >= cap) continue;
    counts.set(item.stage, used + 1);
    kept.push(item);
  }
  return kept;
}

function capRepeatedAmenities<T>(items: Classified<T>[]): Classified<T>[] {
  const counts = new Map<string, number>();
  return items.filter((item) => {
    if (item.stage !== 'otherAmenity' || !item.captionKey) return true;
    const used = counts.get(item.captionKey) ?? 0;
    if (used >= 2) return false;
    counts.set(item.captionKey, used + 1);
    return true;
  });
}

function roomLimit(room: number, perRoom: number, unknownCap: number): number {
  return room === ROOM_DEFAULT ? unknownCap : perRoom;
}

function capPerRoom<T>(items: Classified<T>[], perRoom: number, unknownCap: number): Classified<T>[] {
  const counts = new Map<number, number>();
  return items.filter((item) => {
    const limit = roomLimit(item.room, perRoom, unknownCap);
    const used = counts.get(item.room) ?? 0;
    if (used >= limit) return false;
    counts.set(item.room, used + 1);
    return true;
  });
}

/** One of each room first, then extra angles up to the caps. */
function capApartmentSet<T>(
  items: Classified<T>[],
  maxTotal: number,
  perRoom: number,
  unknownCap: number,
): Classified<T>[] {
  const kept: Classified<T>[] = [];
  const perRoomCount = new Map<number, number>();
  const seen = new Set<Classified<T>>();

  const byRoom = [...items].sort((a, b) => a.room - b.room || a.index - b.index);
  for (const item of byRoom) {
    if (kept.length >= maxTotal) break;
    if ((perRoomCount.get(item.room) ?? 0) >= 1) continue;
    perRoomCount.set(item.room, 1);
    kept.push(item);
    seen.add(item);
  }

  for (const item of items) {
    if (kept.length >= maxTotal) break;
    if (seen.has(item)) continue;
    const limit = roomLimit(item.room, perRoom, unknownCap);
    const used = perRoomCount.get(item.room) ?? 0;
    if (used >= limit) continue;
    perRoomCount.set(item.room, used + 1);
    kept.push(item);
    seen.add(item);
  }

  return kept;
}

function limitApartments<T>(items: Classified<T>[]): Classified<T>[] {
  const nonApartment = items.filter((item) => item.tab !== 'apartment');
  const apartments = items.filter((item) => item.tab === 'apartment');
  const staged = apartments.filter((item) => item.stage === 'staged');
  const rest = apartments.filter((item) => item.stage !== 'staged');
  const limitedStaged = capPerRoom(staged, 3, MAX_UNKNOWN_APARTMENT);
  const limitedRest = capApartmentSet(
    rest,
    staged.length > 0 ? MAX_VACANT_WITH_STAGED : MAX_APARTMENT_WITHOUT_STAGED,
    2,
    staged.length > 0 ? 4 : MAX_UNKNOWN_APARTMENT,
  );
  return [...nonApartment, ...limitedStaged, ...limitedRest];
}

function sortCurated<T>(items: Classified<T>[]): Classified<T>[] {
  return [...items].sort((a, b) => {
    const stageDiff = STAGE_RANK[a.stage] - STAGE_RANK[b.stage];
    if (stageDiff !== 0) return stageDiff;
    if (a.tab === 'apartment' && a.room !== b.room) return a.room - b.room;
    return a.index - b.index;
  });
}

function usablePhotos<T extends CuratableGalleryPhoto>(photos: T[]): T[] {
  return photos.filter((photo) => galleryPhotoSrc(photo) && !isNonMarketingGraphic(photo));
}

/** Split an editor-ordered gallery into tabs without resequencing or dropping photos. */
export function groupGalleryByTab<T extends CuratableGalleryPhoto>(photos: T[]): CuratedGallery<T> {
  const usable = usablePhotos(photos);
  const community: T[] = [];
  const apartment: T[] = [];
  usable.forEach((photo, index) => {
    const item = classify(photo, index);
    if (item.tab === 'community') community.push(photo);
    else if (item.tab === 'apartment') apartment.push(photo);
  });
  return { all: usable, community, apartment };
}

export function curateGalleryPhotos<T extends CuratableGalleryPhoto>(photos: T[]): CuratedGallery<T> {
  const classified = usablePhotos(photos).map((photo, index) => classify(photo, index));
  if (classified.length === 0) return { all: [], community: [], apartment: [] };

  const hero = pickHero(classified);
  const remainder = classified.filter((item) => item !== hero);
  const capped = limitApartments(capRepeatedAmenities(applyStageCaps(remainder)));
  const ordered = sortCurated(capped);

  const all = hero ? [hero, ...ordered] : ordered;
  const community = all.filter((item) => item.tab === 'community');
  const apartment = all.filter((item) => item.tab === 'apartment');

  return {
    all: all.map((item) => item.photo),
    community: community.map((item) => item.photo),
    apartment: apartment.map((item) => item.photo),
  };
}
